import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { SITE } from "@/lib/site";

const AC_LINKS = [
  { to: "/ducted-air-conditioning", label: "Ducted Air Conditioning" },
  { to: "/split-system-air-conditioning", label: "Split System Air Conditioning" },
  { to: "/multi-head-split-systems", label: "Multi-Head Systems" },
];

const NAV = [
  { to: "/", label: "Home" },
  { label: "Air Conditioning", children: AC_LINKS },
  { to: "/veu-rebates", label: "VEU Rebates" },
  { to: "/warranty", label: "Warranty" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const linkCls = ({ isActive }) =>
  `font-display text-[13px] font-bold tracking-wide transition-colors duration-200 ${
    isActive ? "text-brand-blue" : "text-navy hover:text-brand-blue"
  }`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [acOpen, setAcOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setAcOpen(false);
  }, [location.pathname]);

  const acActive = AC_LINKS.some((l) => l.to === location.pathname);

  return (
    <>
      <div className="bg-navy-dark text-white" data-testid="announcement-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-2 text-[11px] font-medium tracking-wide md:text-xs">
          <p className="truncate">
            Serving Victoria Since 2000 <span className="mx-2 text-brand-cyan">•</span> 6,500+
            Installations <span className="mx-2 text-brand-cyan">•</span> Free Quotes
          </p>
          <a
            data-testid="announcement-call-link"
            href={SITE.phoneHref}
            className="hidden items-center gap-2 font-display font-bold text-ice transition-colors duration-200 hover:text-brand-cyan md:inline-flex"
          >
            <Phone className="h-3.5 w-3.5" /> Call {SITE.phone}
          </a>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 border-b bg-white/95 backdrop-blur-md transition-[box-shadow,border-color] duration-300 ${
          scrolled ? "border-line shadow-[0_8px_30px_rgba(5,59,117,0.08)]" : "border-transparent"
        }`}
        data-testid="main-header"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-3">
          <Link to="/" data-testid="header-logo-link" aria-label="Airconditioning Victoria home">
            <img
              src="/assets/logo.png"
              alt="Airconditioning Victoria logo"
              className="h-10 w-auto md:h-12"
              width="220"
              height="48"
            />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">
              Home
            </NavLink>
            <div className="group relative">
              <button
                type="button"
                data-testid="nav-air-conditioning"
                aria-haspopup="true"
                className={`inline-flex items-center gap-1 font-display text-[13px] font-bold tracking-wide transition-colors duration-200 ${
                  acActive ? "text-brand-blue" : "text-navy group-hover:text-brand-blue"
                }`}
              >
                Air Conditioning
                <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-3 opacity-0 transition-[opacity,transform] duration-200 group-hover:visible group-hover:opacity-100">
                <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_20px_50px_rgba(5,59,117,0.14)]">
                  {AC_LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-dropdown-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={({ isActive }) =>
                        `block border-b border-cloud px-5 py-3.5 text-sm font-medium last:border-0 transition-colors duration-150 ${
                          isActive ? "bg-mist text-navy" : "text-ink hover:bg-cloud hover:text-navy"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
            <NavLink to="/veu-rebates" className={linkCls} data-testid="nav-veu-rebates">
              VEU Rebates
            </NavLink>
            <NavLink to="/warranty" className={linkCls} data-testid="nav-warranty">
              Warranty
            </NavLink>
            <NavLink to="/about" className={linkCls} data-testid="nav-about">
              About
            </NavLink>
            <NavLink to="/contact" className={linkCls} data-testid="nav-contact">
              Contact
            </NavLink>
          </nav>

          <div className="hidden items-center gap-5 lg:flex">
            <a
              data-testid="header-phone-link"
              href={SITE.phoneHref}
              className="inline-flex items-center gap-2 font-display text-[13px] font-bold text-navy transition-colors duration-200 hover:text-brand-blue"
            >
              <Phone className="h-4 w-4 text-brand-blue" />
              {SITE.phone}
            </a>
            <Link
              data-testid="header-quote-btn"
              to="/contact"
              className="rounded-[10px] bg-gradient-brand px-5 py-2.5 font-display text-[13px] font-bold tracking-wide text-white shadow-[0_8px_20px_rgba(0,140,196,0.28)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(0,140,196,0.4)]"
            >
              GET A FREE QUOTE
            </Link>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] border border-line text-navy lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div
            className="border-t border-line bg-white px-5 pb-6 pt-3 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col" aria-label="Mobile navigation">
              <NavLink to="/" end className="border-b border-cloud py-3.5 font-display text-sm font-bold text-navy" data-testid="mobile-nav-home">
                Home
              </NavLink>
              <button
                type="button"
                data-testid="mobile-nav-air-conditioning"
                aria-expanded={acOpen}
                onClick={() => setAcOpen(!acOpen)}
                className="flex items-center justify-between border-b border-cloud py-3.5 font-display text-sm font-bold text-navy"
              >
                Air Conditioning
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${acOpen ? "rotate-180" : ""}`} />
              </button>
              {acOpen &&
                AC_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="border-b border-cloud py-3 pl-4 text-sm font-medium text-mutedblue"
                  >
                    {l.label}
                  </NavLink>
                ))}
              <NavLink to="/veu-rebates" className="border-b border-cloud py-3.5 font-display text-sm font-bold text-navy" data-testid="mobile-nav-veu-rebates">
                VEU Rebates
              </NavLink>
              <NavLink to="/warranty" className="border-b border-cloud py-3.5 font-display text-sm font-bold text-navy" data-testid="mobile-nav-warranty">
                Warranty
              </NavLink>
              <NavLink to="/about" className="border-b border-cloud py-3.5 font-display text-sm font-bold text-navy" data-testid="mobile-nav-about">
                About
              </NavLink>
              <NavLink to="/contact" className="border-b border-cloud py-3.5 font-display text-sm font-bold text-navy" data-testid="mobile-nav-contact">
                Contact
              </NavLink>
              <Link
                data-testid="mobile-quote-btn"
                to="/contact"
                className="mt-5 rounded-[10px] bg-gradient-brand px-5 py-3.5 text-center font-display text-sm font-bold text-white"
              >
                GET A FREE QUOTE
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
